import { parseQuestionnaire, QuestionnaireParser } from './questionnaireParser'
import { prefetchValueSets } from './valueSetService'
import { executeQuery } from 'app/api/client/graphqlClient'
import {
    getQuestionnaireDtoQuery,
    listQuestionnaireDtosQuery,
} from 'app/graphql/customQueries'
import { logger } from 'app/logging/Logger'

/** Shape returned by GraphQL QuestionnaireDto queries */
interface QuestionnaireDtoGraphQL {
    id: string
    sourceUrl: string
    name: string
    json: string
    _version: number
    _deleted?: boolean | null
    _lastChangedAt?: number
    createdAt?: string
    updatedAt?: string
}

/**
 * Cache for parsed questionnaires
 * Key: questionnaire type (e.g., "Breast Pathology")
 */
const questionnaireCache = new Map<string, QuestionnaireParser>()

/**
 * Load and parse a Questionnaire by ID
 * This is the preferred method when FormDto has a direct questionnaireId reference
 *
 * @param questionnaireId - The ID of the questionnaire
 * @returns Parsed questionnaire with metadata lookup
 */
export async function loadQuestionnaireById(
    questionnaireId: string
): Promise<QuestionnaireParser | null> {
    // Check cache first (cache by ID)
    const cached = questionnaireCache.get(questionnaireId)
    if (cached) {
        logger.debug('Questionnaire found in cache by ID', {
            action: 'questionnaire_cache_hit',
            metadata: { questionnaireId },
        })
        return cached
    }

    logger.debug('Loading questionnaire by ID via GraphQL', {
        action: 'questionnaire_load_by_id_start',
        metadata: { questionnaireId },
    })

    try {
        // Query via GraphQL by ID
        const questionnaireDto =
            await executeQuery<QuestionnaireDtoGraphQL | null>(
                getQuestionnaireDtoQuery,
                { id: questionnaireId },
                'getQuestionnaireDto'
            )

        if (!questionnaireDto) {
            logger.warn('No questionnaire found for ID', {
                action: 'questionnaire_not_found_by_id',
                metadata: { questionnaireId },
            })
            return null
        }

        logger.debug('Questionnaire loaded via GraphQL by ID', {
            action: 'questionnaire_loaded_by_id',
            metadata: {
                questionnaireId,
                name: questionnaireDto.name,
            },
        })

        // Parse the questionnaire JSON
        const parser = parseQuestionnaire(questionnaireDto.json)

        // Cache by both ID and name for flexibility
        questionnaireCache.set(questionnaireId, parser)
        if (questionnaireDto.name) {
            questionnaireCache.set(questionnaireDto.name, parser)
        }

        logger.debug('Questionnaire parsed and cached by ID', {
            action: 'questionnaire_cached_by_id',
            metadata: {
                questionnaireId,
                itemCount: parser.getAllItems().size,
            },
        })

        return parser
    } catch (error) {
        const err = error instanceof Error ? error : new Error('Unknown error')
        logger.error('Failed to load questionnaire by ID', err, {
            action: 'questionnaire_load_by_id_error',
            metadata: { questionnaireId, error: err.message },
        })
        return null
    }
}

/**
 * Load and parse a Questionnaire by form type
 *
 * @param formType - The type of form (e.g., "Breast Pathology")
 * @returns Parsed questionnaire with metadata lookup
 */
export async function loadQuestionnaireForForm(
    formType: string
): Promise<QuestionnaireParser | null> {
    // Check cache first
    const cached = questionnaireCache.get(formType)
    if (cached) {
        logger.debug('Questionnaire found in cache', {
            action: 'questionnaire_cache_hit',
            metadata: { formType },
        })
        return cached
    }

    logger.debug('Loading questionnaire via GraphQL', {
        action: 'questionnaire_load_start',
        metadata: { formType },
    })

    try {
        // Query GraphQL for questionnaire by name — exact match first
        const exactResult = await executeQuery<{
            items: QuestionnaireDtoGraphQL[]
        }>(
            listQuestionnaireDtosQuery,
            { filter: { name: { eq: formType } }, limit: 1 },
            'listQuestionnaireDtos'
        )

        let questionnaireDto: QuestionnaireDtoGraphQL | undefined =
            exactResult?.items?.find((item) => !item._deleted)

        // If no exact match, try contains match (e.g., "Breast Pathology" matches "Questionnaire-Canshare-Breast-Pathology")
        if (!questionnaireDto) {
            logger.debug('No exact name match, trying contains match', {
                action: 'questionnaire_try_contains',
                metadata: { formType },
            })

            const allResult = await executeQuery<{
                items: QuestionnaireDtoGraphQL[]
            }>(
                listQuestionnaireDtosQuery,
                { limit: 100 },
                'listQuestionnaireDtos'
            )

            questionnaireDto = allResult?.items?.find(
                (item) =>
                    !item._deleted &&
                    item.name?.toLowerCase().includes(formType.toLowerCase())
            )

            if (questionnaireDto) {
                logger.debug('Found questionnaire by contains match', {
                    action: 'questionnaire_found_by_contains',
                    metadata: {
                        formType,
                        matchedName: questionnaireDto.name,
                    },
                })
            }
        }

        if (!questionnaireDto) {
            logger.warn('No questionnaire found for form type', {
                action: 'questionnaire_not_found',
                metadata: { formType },
            })
            return null
        }

        logger.debug('Questionnaire loaded via GraphQL', {
            action: 'questionnaire_loaded',
            metadata: {
                formType,
                questionnaireId: questionnaireDto.id,
            },
        })

        // Parse the questionnaire JSON
        const parser = parseQuestionnaire(questionnaireDto.json)

        // Cache the parsed questionnaire
        questionnaireCache.set(formType, parser)

        logger.debug('Questionnaire parsed and cached', {
            action: 'questionnaire_cached',
            metadata: {
                formType,
                itemCount: parser.getAllItems().size,
            },
        })

        return parser
    } catch (error) {
        const err = error instanceof Error ? error : new Error('Unknown error')
        logger.error('Failed to load questionnaire', err, {
            action: 'questionnaire_load_error',
            metadata: { formType, error: err.message },
        })
        return null
    }
}

/**
 * Load questionnaire and prefetch all its ValueSets
 * This is the recommended way to prepare for form editing
 *
 * Supports both direct ID lookup (preferred) and fallback to name lookup
 *
 * @param formTypeOrId - Either the questionnaire ID (preferred) or form type name
 * @returns Parsed questionnaire (ValueSets are prefetched in background)
 */
export async function loadQuestionnaireWithValueSets(
    formTypeOrId: string,
    userId: string
): Promise<QuestionnaireParser | null> {
    let parser: QuestionnaireParser | null = null

    // Try loading by ID first (if it looks like an ID)
    // IDs are typically UUIDs or have specific patterns
    if (formTypeOrId.length > 20 || formTypeOrId.includes('-')) {
        logger.debug('Attempting to load questionnaire by ID', {
            action: 'questionnaire_load_attempt_by_id',
            metadata: { formTypeOrId },
        })
        parser = await loadQuestionnaireById(formTypeOrId)
    }

    // Fallback to name lookup if ID lookup failed or wasn't attempted
    if (!parser) {
        logger.debug('Loading questionnaire by name', {
            action: 'questionnaire_load_by_name',
            metadata: { formTypeOrId },
        })
        parser = await loadQuestionnaireForForm(formTypeOrId)
    }

    if (!parser) {
        logger.warn('No questionnaire found by ID or name', {
            action: 'questionnaire_not_found',
            metadata: { formTypeOrId },
        })
        return null
    }

    // Prefetch ValueSets in background (don't await)
    const valueSetUrls = parser.getValueSetUrls()

    if (valueSetUrls.length > 0) {
        logger.debug('Prefetching ValueSets for questionnaire', {
            action: 'questionnaire_prefetch_valuesets',
            metadata: { formTypeOrId, valueSetCount: valueSetUrls.length },
        })

        // Prefetch in background
        prefetchValueSets(valueSetUrls, userId).catch(() => {
            logger.warn('Failed to prefetch some ValueSets', {
                action: 'questionnaire_prefetch_failed',
                metadata: { formTypeOrId },
            })
        })
    }

    return parser
}

/**
 * Clear the questionnaire cache
 * Useful for testing or when questionnaires are updated
 */
export function clearQuestionnaireCache(): void {
    questionnaireCache.clear()
    logger.debug('Questionnaire cache cleared', {
        action: 'questionnaire_cache_cleared',
    })
}
